// ─── Figure pipeline ──────────────────────────────────────────────────────────
// Converts raw LLM markdown (with [FIGURE_START]/[TABLE_START] blocks) into:
//   1. Inline <div data-thesis-figure> tokens embedded in chapter HTML
//   2. Rows saved to the thesis_figures table for aggregation/export
//
// This runs ONCE after generation completes (or when Stop/Pause is called).
// Subsequent saves use editor.getHTML() which already contains the tokens.

import { stripMalformedVisuals, tokenizeVisuals, visualTokenDiv, staleTokens, countVisualTokens, parseAndRenderVisuals } from './parseThesisVisuals.js'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { safeDecode } from './exportCompositionEngine.js'

// ── Internal helpers ──────────────────────────────────────────────────────────

function looksLikeMarkdown(text) {
  return /^#{1,4}\s/m.test(text) || /\*\*[^*]+\*\*/m.test(text) || /^\* /m.test(text)
}

const DOMPUR_CONFIG = {
  ADD_TAGS: ['svg', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon',
             'text', 'tspan', 'defs', 'use', 'g', 'marker', 'figure', 'figcaption', 'img'],
  ADD_ATTR: ['viewBox', 'xmlns', 'fill', 'stroke', 'stroke-width', 'stroke-dasharray',
             'marker-end', 'marker-start', 'd', 'cx', 'cy', 'r', 'rx', 'ry',
             'x', 'y', 'x1', 'y1', 'x2', 'y2', 'points', 'transform',
             'text-anchor', 'dominant-baseline', 'font-size', 'font-family',
             'class', 'style', 'width', 'height', 'refX', 'refY', 'orient',
             'markerWidth', 'markerHeight', 'preserveAspectRatio',
             // Figure token data attributes
             'data-thesis-figure', 'data-figure-id', 'data-figure-type',
             'data-figure-code', 'data-figure-title', 'data-figure-caption',
             'data-figure-number', 'data-anchor-hash'],
  FORCE_BODY: true,
}

async function markdownToHTML(content) {
  if (!content) return ''
  const withVisuals = parseAndRenderVisuals(content) // handles residual [FIGURE_START] if any
  if (looksLikeMarkdown(withVisuals)) {
    const html = await marked.parse(withVisuals, { async: false })
    return DOMPurify.sanitize(html, DOMPUR_CONFIG)
  }
  return DOMPurify.sanitize(withVisuals, DOMPUR_CONFIG)
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Build an inline figure token for an SVG chart generated by the Research Lab.
 * The SVG string is URL-encoded and embedded in the data-figure-code attribute,
 * making the token fully self-contained (no separate storage lookup required).
 *
 * @param {{ token: string, svgCode: string, title: string, caption: string }} opts
 * @returns {string} HTML string — a <div data-thesis-figure> token
 */
export function buildSVGFigureToken({ token, svgCode, title = '', caption = '' }) {
  return visualTokenDiv(token, 'chart-svg', svgCode, title, caption)
}

/**
 * Inject a figure token into chapter HTML immediately after the paragraph
 * that most strongly matches the given cue patterns.
 * Falls back to appending at the end if no match is found.
 */
export function injectFigureAfterParagraph(html, figureTokenHTML, cuePatterns = [], fallbackFraction = null) {
  // Paragraph/block closing tags to search after
  const BLOCK_CLOSE = /(<\/p>|<\/li>|<\/blockquote>)/gi

  let bestIdx = -1
  let bestScore = -1
  let match
  const closes = []

  BLOCK_CLOSE.lastIndex = 0
  while ((match = BLOCK_CLOSE.exec(html)) !== null) {
    const idx = match.index + match[0].length
    closes.push(idx)
    const before = html.slice(Math.max(0, match.index - 500), match.index)
    const score = cuePatterns.reduce((s, re) => {
      const copy = new RegExp(re.source, re.flags.replace('g', ''))
      return s + (copy.test(before) ? 1 : 0)
    }, 0)
    if (score > bestScore) {
      bestScore = score
      bestIdx = idx
    }
  }

  const figWrapper = `\n<div class="thesis-figure-anchor" style="margin:1.5rem 0">${figureTokenHTML}</div>`

  if (bestIdx !== -1 && bestScore > 0) {
    return html.slice(0, bestIdx) + figWrapper + html.slice(bestIdx)
  }

  // No cue matched (or none supplied). A caller placing several visuals at
  // once passes an increasing fraction here so they land spread across the
  // chapter instead of all piling onto the final paragraph.
  if (fallbackFraction != null && closes.length) {
    const pos = closes[Math.min(closes.length - 1, Math.floor(fallbackFraction * closes.length))]
    return html.slice(0, pos) + figWrapper + html.slice(pos)
  }

  return html + figWrapper
}

// ── Fill missing visuals ──────────────────────────────────────────────────────
// Places figures/tables the model wrote in response to a repair-only prompt
// (see backend fillVisuals) into an already-finished chapter without touching
// its existing prose. Each new visual is scored against the paragraphs of the
// chapter using words from its own title/caption — the same idea as the
// Conceptual/Research Figures pipelines — and falls back to an even spread
// across the chapter when nothing scores, rather than piling everything at
// the end.

function attrFromToken(tokenHTML, name) {
  const m = tokenHTML.match(new RegExp(`data-${name}="([^"]*)"`))
  return m ? safeDecode(m[1]) : ''
}

function cuesFromText(text) {
  const words = (text || '').split(/[^A-Za-z]+/).filter(w => w.length > 4)
  const uniq  = [...new Set(words.map(w => w.toLowerCase()))].slice(0, 6)
  return uniq.map(w => new RegExp(w, 'i'))
}

/**
 * @param {string} rawFillText   Model output for a repair-only prompt — pure
 *                                [FIGURE_START]/[TABLE_START] blocks, no prose.
 * @param {string} existingHtml  The chapter's current full HTML.
 * @param {object} supabaseClient
 * @param {{ thesisId, chapterId, chapterNum }} opts
 * @returns {Promise<string>} existingHtml with the new visuals inserted
 */
export async function fillMissingVisuals(rawFillText, existingHtml, supabaseClient, opts) {
  const { thesisId, chapterId, chapterNum = 1 } = opts
  const chapterKey = String(chapterId || '').replace(/-/g, '').slice(0, 8)
    || `x${Math.random().toString(36).slice(2, 10)}`
  const startAt = countVisualTokens(existingHtml)

  // Unlike processFiguresFromMarkdown, this input is guaranteed to be pure
  // [FIGURE_START]/[TABLE_START] blocks with no surrounding prose — so there
  // is nothing here for a markdown→HTML pass to do, and skipping it means
  // this whole function stays plain, DOM-free JS.
  const { processed, rows: tokenRows } = tokenizeVisuals(rawFillText, { thesisId, chapterId, chapterNum, chapterKey, startAt })
  const cleaned = stripMalformedVisuals(processed)

  if (tokenRows.length > 0 && supabaseClient) {
    try {
      await supabaseClient.from('thesis_figures').upsert(tokenRows, { onConflict: 'thesis_id,token' })
    } catch (err) {
      console.error('[figurePipeline] DB upsert failed:', err)
    }
  }

  const newTokens = cleaned.match(/<div[^>]*data-thesis-figure="true"[^>]*><\/div>/gi) || []
  if (!newTokens.length) return existingHtml

  let html = existingHtml
  const total = newTokens.length
  newTokens.forEach((tokenHTML, i) => {
    const title   = attrFromToken(tokenHTML, 'figure-title')
    const caption = attrFromToken(tokenHTML, 'figure-caption')
    const cues    = cuesFromText(`${title} ${caption}`)
    html = injectFigureAfterParagraph(html, tokenHTML, cues, (i + 1) / (total + 1))
  })

  await syncChapterFigures(supabaseClient, { chapterId: opts.chapterId, html })
  return html
}

/**
 * Process raw generated markdown:
 *  - Extract [FIGURE_START]/[TABLE_START] blocks
 *  - Replace with inline <div data-thesis-figure> tokens
 *  - Save rows to thesis_figures via supabaseClient
 *  - Convert remaining markdown to HTML
 *
 * @param {string}  rawMarkdown   Accumulated generation output
 * @param {object}  supabaseClient  Supabase JS client
 * @param {object}  opts          { thesisId, chapterId, chapterNum }
 * @returns {Promise<string>}     Processed HTML with inline figure tokens
 */
export async function processFiguresFromMarkdown(rawMarkdown, supabaseClient, opts) {
  const { thesisId, chapterId, chapterNum = 1, startAt } = opts
  // Stable per-chapter key for token ids. Falls back to a random suffix when no
  // chapterId is available, which is still unique — unlike chapterNum, which
  // defaults to 1 and produced "fig_1_1" in every chapter of a thesis.
  const chapterKey = String(chapterId || '').replace(/-/g, '').slice(0, 8)
    || `x${Math.random().toString(36).slice(2, 10)}`

  const { processed, rows: tokenRows } = tokenizeVisuals(rawMarkdown, { thesisId, chapterId, chapterNum, chapterKey, startAt })

  // 5. Persist rows to DB (upsert on thesis_id + token)
  if (tokenRows.length > 0 && supabaseClient) {
    try {
      await supabaseClient
        .from('thesis_figures')
        .upsert(tokenRows, { onConflict: 'thesis_id,token' })
    } catch (err) {
      console.error('[figurePipeline] DB upsert failed:', err)
      // Non-fatal — figures still render from inline HTML attrs
    }
  }

  // 6. Strip any remaining malformed [Diagram: …] text placeholders
  const cleaned = stripMalformedVisuals(processed)

  // 7. Convert markdown prose to HTML (figure token divs pass through as raw HTML)
  const html = await markdownToHTML(cleaned)

  return html
}

/**
 * Drop figure rows this chapter no longer contains.
 *
 * Rows were only ever upserted, never removed. A chapter regenerated with fewer
 * visuals than the run before kept every row from the earlier run, so the List
 * of Figures listed diagrams that were nowhere in the chapter — and each
 * regeneration widened the gap instead of closing it, which is exactly what
 * made regenerating feel like it never fixed anything.
 *
 * Reads the chapter's tokens first and deletes by explicit list, so a token the
 * scanner does not recognise can never take rows with it.
 *
 * @param {object} supabaseClient
 * @param {{ chapterId: string, html: string }} opts  html = the chapter's final content
 */
export async function syncChapterFigures(supabaseClient, { chapterId, html }) {
  if (!supabaseClient || !chapterId) return
  try {
    const { data, error } = await supabaseClient
      .from('thesis_figures')
      .select('token')
      .eq('chapter_id', chapterId)
    if (error) throw error
    const stale = staleTokens((data || []).map(r => r.token), html)
    if (!stale.length) return
    await supabaseClient.from('thesis_figures').delete().eq('chapter_id', chapterId).in('token', stale)
  } catch (err) {
    // Non-fatal: the chapter still renders from its inline tokens.
    console.error('[figurePipeline] stale figure cleanup failed:', err)
  }
}

/**
 * Build export HTML for a single figure/table node (used by export engine).
 * Replaces the <div data-thesis-figure> with a rendered mermaid block or table.
 */
export function buildFigureExportBlock(attrs) {
  const { figureType, code, title, caption } = attrs

  // ── SVG chart figure (Research Lab generated) ─────────────────────────────
  if (figureType === 'chart-svg') {
    return `<figure class="thesis-fig" style="margin:2rem 0;text-align:center;page-break-inside:avoid">
${title ? `<p style="font-weight:700;font-size:0.9rem;text-align:center;margin-bottom:0.5rem">${title}</p>` : ''}
<div style="display:inline-block;max-width:100%">${code}</div>
${caption ? `<figcaption style="font-style:italic;font-size:0.8rem;color:#475569;text-align:center;margin-top:0.75rem">${caption}</figcaption>` : ''}
</figure>`
  }

  if (figureType === 'table') {
    // Render markdown table as HTML table for export
    const lines = (code || '').trim().split('\n').filter(l => l.trim() && !l.match(/^\s*[-|]+\s*$/))
    const parseRow = (line) => line.split('|').map(c => c.trim()).filter(Boolean)
    const [headerLine, ...bodyLines] = lines
    const headers = parseRow(headerLine || '')
    const thead = headers.length
      ? `<thead><tr>${headers.map(h => `<th style="background:#f0ece4;font-weight:600;border:1px solid #c5b89a;padding:8px 12px">${h}</th>`).join('')}</tr></thead>`
      : ''
    const tbody = bodyLines.length
      ? `<tbody>${bodyLines.map(row => `<tr>${parseRow(row).map(c => `<td style="border:1px solid #c5b89a;padding:8px 12px">${c}</td>`).join('')}</tr>`).join('')}</tbody>`
      : ''
    return `<figure class="thesis-fig" style="margin:2rem 0;padding:1.25rem;background:#f8f6f2;border:1px solid #d9d0c3;border-radius:6px">
${title ? `<p style="font-weight:700;font-size:0.9rem;text-align:center;margin-bottom:0.75rem">${title}</p>` : ''}
<div style="overflow-x:auto"><table style="width:100%;border-collapse:collapse;font-size:14px">${thead}${tbody}</table></div>
${caption ? `<p style="font-style:italic;font-size:0.8rem;color:#64748b;text-align:center;margin-top:0.75rem">${caption}</p>` : ''}
</figure>`
  }

  // Mermaid diagram
  const escaped = (code || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return `<figure class="thesis-fig" style="margin:2rem 0;padding:1.25rem;background:#f8f6f2;border:1px solid #d9d0c3;border-radius:6px;text-align:center">
${title ? `<p style="font-weight:700;font-size:0.9rem;text-align:center;margin-bottom:0.5rem">${title}</p>` : ''}
<pre class="mermaid" style="background:#f8f6f2;border:none;padding:0.5rem;text-align:left;overflow-x:auto">${escaped}</pre>
${caption ? `<p style="font-style:italic;font-size:0.8rem;color:#64748b;text-align:center;margin-top:0.75rem">${caption}</p>` : ''}
</figure>`
}

/**
 * Replace <div data-thesis-figure> tokens in HTML with rendered visual blocks.
 * Used by the export engine to produce a self-contained HTML file.
 */
export function expandFigureTokens(html) {
  return html.replace(
    /<div data-thesis-figure="true"([^>]*)><\/div>/g,
    (match, attrsStr) => {
      const get = (name) => {
        const m = attrsStr.match(new RegExp(`data-${name}="([^"]*)"`, 'i'))
        return m ? safeDecode(m[1]) : ''
      }
      return buildFigureExportBlock({
        figureType: get('figure-type') || 'mermaid',
        code:       get('figure-code'),
        title:      get('figure-title'),
        caption:    get('figure-caption'),
      })
    }
  )
}
